import { useEffect } from 'react';
import './App.css';
import ImprovedEnterpriseLayoutPreview from './Layout/main_layout';
import LoginInterface from './login-interface';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { GlobalErrorHandler } from './components/common/GlobalErrorHandler';
import { SnackbarProvider } from './components/common/SnackbarNotification';
import { TabTakeoverGuard } from './components/common/TabTakeoverGuard';
import {
  clearAllAuthStorage,
  initCrossTabLogin,
  initCrossTabLogout,
  installBackNavigationGuard,
} from './utils/authGuard';
import { tokenUtils } from './api/config/apiConfig';

/**
 * Listens for logout broadcasts from other tabs (via BroadcastChannel /
 * `storage` event). When another tab logs out, wipe local state and bounce
 * this tab back to the login route so the whole app is signed out in sync.
 *
 * When we ARE on the login route, additionally trap the browser back button
 * so users can't navigate back into a cached authenticated page after logout.
 */
function AuthLifecycle() {
  const location = useLocation();
  const isLoginRoute = location.pathname === '/';

  useEffect(() => {
    return initCrossTabLogout(() => {
      clearAllAuthStorage();
      if (window.location.pathname !== '/') {
        window.location.replace('/');
      }
    });
  }, []);

  useEffect(() => {
    return initCrossTabLogin(() => {
      // Only hop tabs sitting on the login page — don't yank users out of
      // whatever authenticated page they're currently on.
      if (window.location.pathname === '/') {
        window.location.replace('/Layout/main_layout');
      }
    });
  }, []);

  useEffect(() => {
    if (!isLoginRoute) return;
    return installBackNavigationGuard();
  }, [isLoginRoute]);

  return null;
}

/**
 * Mounts the single-tab presence guard only when the user is not on the
 * login route. Pre-auth double-tabs coexist freely (helpful when someone
 * accidentally opens the URL twice before signing in); post-auth enforcement
 * kicks in the moment the layout renders.
 */
function TabPresenceGate() {
  const location = useLocation();
  if (location.pathname === '/') return null;
  return <TabTakeoverGuard />;
}

function App() {
  return (
    <GlobalErrorHandler>
      <ErrorBoundary
        showNotification={true}
        logError={true}
        errorMessage="The application encountered an error. Please try again."
      >
        <SnackbarProvider>
          <AuthLifecycle />
          <TabPresenceGate />
          <Routes>
            <Route
              path="/"
              element={
                tokenUtils.isAuthenticated()
                  ? <Navigate to="/Layout/main_layout" replace />
                  : <LoginInterface />
              }
            />
            <Route path="/Layout/main_layout" element={<ImprovedEnterpriseLayoutPreview />} />
            {/* other routes */}
          </Routes>
        </SnackbarProvider>
      </ErrorBoundary>
    </GlobalErrorHandler>
  );
}

export default App;
